import {
    Children,
    type ComponentProps,
    type ComponentType,
    createElement,
    Fragment,
    type HTMLAttributes,
    type ReactNode,
} from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import cn from 'classnames';
import { Header, Image, Link, type LinkProps, Text } from '@direct-frontend/components';
import { logEventString } from '@adv-frontend/yastore-modules/logger';
import { type MarkdownLinkClickParams, type MarkdownProps } from './Markdown.types';
import styles from './Markdown.module.css';

export type { MarkdownProps } from './Markdown.types';

export function withMarkdownProps<ExtraProps, OriginalProps>(
    WrappedComponent: ComponentType<OriginalProps>,
    extraProps: ExtraProps
): ComponentType<Omit<HTMLAttributes<HTMLElement>, 'color'>> {
    const Wrapper = (props: Omit<HTMLAttributes<HTMLElement>, 'color'>) =>
        createElement(WrappedComponent as never, { ...extraProps, ...props });

    return Wrapper;
}

const createMarkdownComponents = (
    linkTrackingName: string | undefined,
    size: MarkdownProps['size'],
    role: MarkdownProps['role'],
    onLinkClick?: (params: MarkdownLinkClickParams) => void
): Partial<Components> & Record<string, unknown> => {
    const paragraph = withMarkdownProps(Text, { size, role });

    const components = {
        h1: withMarkdownProps(Header, { level: 'h1' }),
        h2: withMarkdownProps(Header, { level: 'h2' }),
        h3: withMarkdownProps(Header, { level: 'h3' }),
        h4: withMarkdownProps(Header, { level: 'h4' }),
        h5: withMarkdownProps(Header, { level: 'h5' }),
        h6: withMarkdownProps(Header, { level: 'h5' }),

        p: paragraph,
        a: ({ href, ...props }: ComponentProps<'a'>) => {
            const isExternalLink = Boolean(href?.startsWith('http') || href?.startsWith('//'));

            return (
                <Link
                    {...(props as LinkProps)}
                    href={href}
                    rel={isExternalLink ? 'noopener noreferrer nofollow' : undefined}
                    target="_blank"
                    className={styles.inline}
                    onClick={() => {
                        if (linkTrackingName) {
                            logEventString(`${linkTrackingName}.LinkClick`, `href: ${href}`);
                        }

                        if (href) {
                            onLinkClick?.({ href });
                        }
                    }}
                >
                    {props.children}
                </Link>
            );
        },

        strong: withMarkdownProps(Text, { size, role, bold: true, className: styles.inline }),
        em: withMarkdownProps(Text, { size, role, it: true, className: cn(styles.inline, styles.italic) }),

        blockquote: (props: ComponentProps<'blockquote'>) => (
            <blockquote className={styles.blockquote}>{props.children}</blockquote>
        ),

        img: withMarkdownProps(Image, { width: '100%', height: 'auto', className: styles.image }),

        ul: (props: ComponentProps<'ul'>) => (
            <Text size={size} role={role}>
                <ul className={styles.list}>{props.children}</ul>
            </Text>
        ),
        ol: ({ start, children }: ComponentProps<'ol'>) => (
            <Text size={size} role={role}>
                <ol className={styles.orderedList} start={start}>
                    {children}
                </ol>
            </Text>
        ),
        li: (props: ComponentProps<'li'>) => {
            const children = Children.toArray(props.children).filter(
                (child) => typeof child !== 'string' || child.trim() !== '' || !child.includes('\n')
            );

            return <li className={styles.listItem}>{children}</li>;
        },
    };

    return components as Partial<Components> & Record<string, unknown>;
};

export default function Markdown(props: MarkdownProps) {
    const { children, className, 'data-testid': dataTestId, linkTrackingName, size = 's', role, onLinkClick } = props;

    const markdownComponents = createMarkdownComponents(linkTrackingName, size, role, onLinkClick);

    return (
        <div className={cn(styles.root, className)} data-testid={dataTestId}>
            <ReactMarkdown components={markdownComponents}>{children}</ReactMarkdown>
        </div>
    );
}

const block = ({ children }: { children?: ReactNode }) => (
    <Fragment>
        {children}
        {'\n'}
    </Fragment>
);
const inline = ({ children }: { children?: ReactNode }) => <Fragment>{children}</Fragment>;

const plainTextComponents: Partial<Components> = {
    h1: block,
    h2: block,
    h3: block,
    h4: block,
    h5: block,
    h6: block,
    p: block,
    blockquote: block,
    li: block,
    ul: inline,
    ol: inline,
    a: inline,
    strong: inline,
    em: inline,
    img: () => null,
};

export function MarkdownPlainText({ children }: Pick<MarkdownProps, 'children'>) {
    return <ReactMarkdown components={plainTextComponents}>{children}</ReactMarkdown>;
}
